import { css, html, LitElement, nothing } from "lit";
import {
  customElement,
  property,
  query,
  queryAll,
  state,
} from "lit/decorators.js";
import { when } from "lit/directives/when.js";

import { MQL_LG } from "../../constants";
import { config } from "../../modules/config";
import { getNextFocusIndex } from "../../utils/a11y";
import { debounce } from "../../utils/debounce";
import {
  SEARCH_CLOSE_EVENT_NAME,
  SEARCH_OPEN_EVENT_NAME,
} from "./global-header.constants";
import { autocomplete } from "./global-header.helpers";

@customElement("bp-global-header-search")
export class GlobalHeaderSearch extends LitElement {
  static styles = css`
    :host {
      /* TODO Remove once color palette is updated */
      --color-neutral-00: #ffffff;
      --color-neutral-10: #f6f5f4;
      --color-neutral-20: #edeceb;

      display: block;
    }

    #search[data-active] {
      background-color: var(--global-header-background-color);
    }

    #search-bar {
      background-color: inherit;
      padding-block: var(--size-1);
      padding-inline: var(--size-3) var(--size-7);
      box-sizing: border-box;
      position: absolute;
      inset-inline-start: 0;
      inset-block-start: 0;
      width: 100%;
    }

    #search-bar[inert] {
      pointer-events: none;
      z-index: -1;
    }

    #search-bar[inert] form {
      opacity: 0;
    }

    form {
      border-radius: var(--radius-full);
      background-color: var(--color-neutral-20);
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: var(--size-1);
      align-items: center;
      padding-block: var(--size-0-5);
      padding-inline: var(--size-2);
      height: var(--size-4);
      opacity: 1;
    }

    form bp-icon {
      color: var(--color-neutral-40);
    }

    button {
      border: 0;
      border-radius: var(--radius-md);
      background: transparent;
      appearance: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: var(--size-1);
      font-family: var(--font-sans);
      font-size: var(--font-size-md);
      font-weight: var(--font-weight-semibold);
      user-select: none;
      cursor: pointer;
      color: currentColor;
      height: var(--size-7);
      padding-block: var(--size-1-5);
      padding-inline: var(--size-1-5);
    }

    button:disabled {
      filter: grayscale(100%);
      opacity: var(--opacity-50);
      pointer-events: none;
    }

    button[inert] {
      visibility: hidden;
    }

    button:focus-visible {
      outline: var(--a11y-outline);
      outline-offset: calc(var(--size-0-5) * -1);

      @media (min-width: 1024px) {
        outline-offset: unset;
      }
    }

    button:focus:not(:focus-visible) {
      outline: none;
    }

    input {
      appearance: none;
      background-color: transparent;
      border: 0;
      font-size: var(--font-size-md);
      line-height: var(--line-height-tight);
      min-width: 0;
      padding: 0;
    }

    input:focus {
      outline: none;
    }

    input::-webkit-search-cancel-button {
      appearance: none;
    }

    #results {
      display: grid;
      gap: var(--size-2);
      padding-block: var(--size-2);
    }

    #results .items a {
      text-decoration: none;
      color: currentColor;
    }

    #results ul {
      list-style-type: none;
      margin: 0;
      padding: 0;
      width: 100%;
    }

    #results li {
      border-radius: var(--radius-md);
      cursor: pointer;
    }

    #results li[aria-selected="true"] {
      background-color: var(--color-neutral-05);
    }

    #items a {
      display: block;
      padding: var(--size-1) var(--size-2);
    }

    .label {
      text-transform: uppercase;
      letter-spacing: var(--letter-spacing-wide);
      font-weight: var(--font-weight-semibold);
      font-size: var(--font-size-sm);
      color: var(--color-gray);
      margin: var(--size-1);
      margin-block-end: var(--size-2);
    }

    @media (min-width: 1024px) {
      #search {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        position: relative;
      }

      #search-bar {
        position: absolute;
        padding-block: 2px;
        padding-inline: 0;
      }

      #results {
        padding: 0;
      }

      button {
        padding-inline: var(--size-1);
        height: auto;
      }

      bp-global-header-submenu {
        position: absolute;
        inset-inline: 0;
        inset-block-start: var(--size-5);
      }
    }

    @media (hover: hover) {
      #results li:hover {
        background-color: var(--color-neutral-05);
      }
    }
  `;

  @query("form")
  $form?: HTMLFormElement;

  @query("input")
  $input?: HTMLInputElement;

  @query("#open-button")
  $openButton?: HTMLButtonElement;

  @queryAll("#results li")
  $resultsListItems?: NodeListOf<HTMLElement>;

  @query("#search")
  $search?: HTMLElement;

  _activeItemIndex = -1;

  @state()
  data: null | string[] = null;

  handleInput = debounce(() => {
    const value = this.$input?.value;

    // Require a minimum length of 2 characters for search
    if (value && value.length > 1) {
      this.performSearch(value);
    } else {
      this.clearResults();
    }
  }, 300);

  @state()
  isActive = false;

  @property({ attribute: "label" })
  label = "";

  @property({ attribute: "label-close" })
  labelClose = "";

  @property({ attribute: "placeholder" })
  placeholder = "";

  #desktopMql = MQL_LG;

  @state()
  useDesktopLayout = this.#desktopMql.matches;

  get activeItemIndex() {
    return this._activeItemIndex;
  }

  set activeItemIndex(newVal: number) {
    this._activeItemIndex = newVal;

    this.$resultsListItems?.forEach((x, i) => {
      if (i === newVal) {
        x.setAttribute("aria-selected", "true");
        x.focus();
      } else {
        x.removeAttribute("aria-selected");
      }
    });
  }

  get hasResults() {
    return this.data?.length;
  }

  constructor() {
    super();

    this.#desktopMql.addEventListener(
      "change",
      this.handleDesktopMediaQueryChange,
    );

    this.addEventListener("keydown", this.handleKeyDown as EventListener);
  }

  activate() {
    this.isActive = true;

    // Wait for inert attribute changes to apply
    this.updateComplete.then(() => {
      this.$input?.focus();
    });

    document.addEventListener("click", this.handleDocumentClick);
  }

  clearResults() {
    this.activeItemIndex = -1;
    this.data = null;
  }

  deactivate() {
    this.isActive = false;
    this.reset();
    document.removeEventListener("click", this.handleDocumentClick);
  }

  focusNextResult() {
    if (!this.data) return;

    this.activeItemIndex = getNextFocusIndex(
      "next",
      this.activeItemIndex,
      this.$resultsListItems?.length ?? 0,
    );
  }

  focusPrevResult() {
    if (!this.data) return;

    this.activeItemIndex = getNextFocusIndex(
      "prev",
      this.activeItemIndex,
      this.$resultsListItems?.length ?? 0,
    );
  }

  getSearchPageUrl(query: string) {
    const params = new URLSearchParams({ q: query }).toString();

    const lang = config.language;

    // TODO: Remove this once proyectobiblia.com is migrated. Per Luke's suggestion:
    // https://github.com/thebibleproject/web/pull/2020
    if (
      lang &&
      lang !== "en" &&
      !/proyectobiblia\.com/.test(window.location.hostname)
    ) {
      return `/${lang}/search/?${params}`;
    }

    return `/search/?${params}`;
  }

  gotoResultsPage(query: string) {
    const href = this.getSearchPageUrl(query);

    window.location.assign(href);
  }

  handleCloseButtonClick = (e: MouseEvent) => {
    e.preventDefault();
    this.emitClose();
  };

  handleDesktopMediaQueryChange = ({ matches }: MediaQueryListEvent) => {
    this.useDesktopLayout = matches;
  };

  handleDocumentClick = (e: MouseEvent) => {
    const shouldClose = !e.composedPath().includes(this);

    if (shouldClose) {
      this.emitClose();
    }
  };

  handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.focusNextResult();

        return;
      case "ArrowUp":
        e.preventDefault();
        this.focusPrevResult();

        return;
      case "Escape":
        this.emitClose();

        this.updateComplete.then(() => {
          this.$openButton?.focus();
        });

        return;
      case "Tab":
        this.activeItemIndex = -1;

        return;
      default:
        return;
    }
  };

  handleSubmit = (e: SubmitEvent) => {
    e.preventDefault();

    const $item = this.$resultsListItems?.item(this.activeItemIndex);
    const href = $item?.querySelector("a")?.getAttribute("href");

    // Follow the link if the item is a link
    if (href) {
      window.location.assign(href);

      return;
    }

    const query = this.$input?.value;

    // Use the input value as the query for the search page
    if (query) {
      this.gotoResultsPage(query);
    }
  };

  async performSearch(query: string) {
    this.activeItemIndex = -1;
    this.data = await autocomplete(query);
  }

  render() {
    return html`
      <div id="search" ?data-active=${this.isActive}>
        <div id="search-bar" ?inert=${!this.isActive}>
          <form @submit="${this.handleSubmit}">
            <bp-icon icon="magnifying-glass"></bp-icon>
            <input
              type="search"
              enterkeyhint="search"
              autocomplete="off"
              aria-autocomplete="list"
              aria-controls="results"
              aria-expanded=${this.hasResults}
              placeholder="${this.placeholder}"
              @input=${this.handleInput}
            />
          </form>

          ${when(
            this.hasResults,
            () => html`
              <bp-global-header-submenu>
                <div id="results">
                  <div id="items">${this.renderLinks()}</div>
                </div>
              </bp-global-header-submenu>
            `,
          )}
        </div>

        <button
          aria-label=${this.label}
          id="open-button"
          ?inert=${this.isActive}
          @click=${this.emitOpen}
        >
          <bp-icon icon="magnifying-glass"></bp-icon>
        </button>
      </div>
    `;
  }

  renderLinks() {
    if (!this.data?.length) {
      return nothing;
    }

    return html`
      <div class="items">
        <ul role="listbox">
          ${this.data?.map(
            (x) => html`
              <li role="option">
                <a href="${this.getSearchPageUrl(x)}">${x}</a>
              </li>
            `,
          )}
        </ul>
      </div>
    `;
  }

  reset() {
    this.$form?.reset();
    this.clearResults();
  }

  private emitClose() {
    this.dispatchEvent(
      new CustomEvent(SEARCH_CLOSE_EVENT_NAME, {
        composed: true,
      }),
    );
  }

  private emitOpen() {
    this.dispatchEvent(
      new CustomEvent(SEARCH_OPEN_EVENT_NAME, {
        composed: true,
      }),
    );
  }
}
